import type BackOfficeConnector from "../data/backOfficeConnector";
import type BulkExemption from "../entities/bulkExemption";
import ExemptionResponse from "../entities/exemptionResponse";
import type EventBus from "../services/eventBus";
import { serializeFilters } from "utils/filters";
import RepositoryBase from "./repositoryBase";
import type CreateExemptionRequest from "../entities/createExemptionRequest";
import type ExemptionsRequest from "../entities/exemptionsRequest";
import type { IPagination } from "../data/types";
import ExemptionsTotalCountResponse from "entities/exemptionsTotalCountResponse";
import type SearchExemptionsRequest from "entities/searchExemptionsRequest";
import ExemptionDetailGroupsResponse from "entities/exemptionDetailGroupsResponse";
import ExemptionChangeResponse from "entities/exemptionChangeResponse";
import ExemptionPreviewResponse from "entities/exemptionPreviewResponse";
import type ExemptionChangeRequest from "entities/exemptionChangeRequest";
import ExemptionChangePreviewResponse from "entities/exemptionChangePreviewResponse";
import type { ExemptionCancellationRequest } from "entities/exemptionCancellationRequest";

export default class ExemptionRepository extends RepositoryBase<BackOfficeConnector> {
  constructor(protected apiFactory: ReturnType<typeof BackOfficeConnector.Factory>, protected eventBus: EventBus) {
    super(apiFactory, eventBus);
  }

  getAll(filters: ExemptionsRequest, pagination: IPagination, abortSignal?: AbortSignal) {
    return this.callApi(
      api =>
        api
          .all("call_center")
          .all("exemption_search")
          .postPagedEntities<ExemptionResponse>(
            { ...serializeFilters(filters, false), ...pagination },
            ExemptionResponse,
            abortSignal
          ),
      {
        ownApiFactory: () => this.apiFactory({ customVersion: "2" }),
      }
    );
  }

  getOne(exemptionId: string) {
    return this.callApi(api => api.all("call_center").one("exemptions", exemptionId).getEntity(ExemptionDetailGroupsResponse), {
      ownApiFactory: () => this.apiFactory({ customVersion: "2" }),
    });
  }

  getAllChanges(exemptionId: string) {
    return this.callApi(
      api => api.all("call_center").one("exemptions", exemptionId).all("changes").getEntities(ExemptionChangeResponse),
      {
        ownApiFactory: () => this.apiFactory({ customVersion: "2" }),
      }
    );
  }

  create(entity: CreateExemptionRequest) {
    return this.callApi(api => api.all("call_center").all("exemptions").postOnly(entity), {
      ownApiFactory: () => this.apiFactory({ customVersion: "2" }),
    });
  }

  createPreview(entity: CreateExemptionRequest) {
    return this.callApi(
      api =>
        api.all("call_center").all("exemption_preview").postEntity<ExemptionPreviewResponse>(entity, ExemptionPreviewResponse),
      {
        ownApiFactory: () => this.apiFactory({ customVersion: "2" }),
      }
    );
  }

  totalCount(entity: SearchExemptionsRequest) {
    return this.callApi(
      api =>
        api
          .all("call_center")
          .all("exemption_search")
          .all("total_count")
          .postEntity<ExemptionsTotalCountResponse>({ ...serializeFilters(entity, false) }, ExemptionsTotalCountResponse),
      {
        ownApiFactory: () => this.apiFactory({ customVersion: "2" }),
      }
    );
  }

  cancellation(exemptionId: string, exemption: ExemptionCancellationRequest) {
    return this.callApi(api => api.all("call_center").one("exemptions", exemptionId).all("cancellation").postOnly(exemption), {
      ownApiFactory: () => this.apiFactory({ customVersion: "2" }),
    });
  }

  update(exemptionId: string, exemption: ExemptionChangeRequest) {
    return this.callApi(api => api.all("call_center").one("exemptions", exemptionId).putOnly(exemption), {
      ownApiFactory: () => this.apiFactory({ customVersion: "2" }),
    });
  }

  updatePreview(exemptionId: string, entity: ExemptionChangeRequest) {
    entity.exemptionId = undefined;
    delete entity.vehicle.country;
    return this.callApi(
      api =>
        api
          .all("call_center")
          .one("exemptions", exemptionId)
          .all("change_preview")
          .postEntity<ExemptionChangePreviewResponse>(entity, ExemptionChangePreviewResponse),
      {
        ownApiFactory: () => this.apiFactory({ customVersion: "2" }),
      }
    );
  }

  deleteItem(exemptionId: string) {
    return this.callApi(api => api.all("call_center").one("exemptions", exemptionId).delete({}), {
      ownApiFactory: () => this.apiFactory({ customVersion: "2" }),
    });
  }

  bulkCreate(entity: BulkExemption) {
    const formData = new FormData();
    formData.set("official_record_id", entity.officialRecordId);
    if (entity.excelFile) {
      formData.set("file", entity.excelFile);
    }
    if (entity.exemptionReasonId) {
      formData.set("exemption_reason_id", entity.exemptionReasonId);
    }

    return this.callApi(api => api.all("call_center").all("bulk_exemptions").postData(formData), {
      ownApiFactory: () => this.apiFactory({ customVersion: "2" }),
      useGenericErrorMessageHandler: false,
    });
  }

  bulkCreatePreview(entity: BulkExemption) {
    const formData = new FormData();
    formData.set("official_record_id", entity.officialRecordId);
    if (entity.excelFile) {
      formData.set("file", entity.excelFile);
    }
    if (entity.exemptionReasonId) {
      formData.set("exemption_reason_id", entity.exemptionReasonId);
    }

    return this.callApi(
      api =>
        api
          .all("call_center")
          .all("bulk_exemption_preview")
          .postData(formData)
          .then(response => response.json()),
      {
        ownApiFactory: () => this.apiFactory({ customVersion: "2" }),
        useGenericErrorMessageHandler: false,
      }
    );
  }

  bulkTerminate(entity: BulkExemption) {
    const formData = new FormData();
    formData.set("official_record_id", entity.officialRecordId);
    if (entity.excelFile) {
      formData.set("file", entity.excelFile);
    }
    if (entity.exemptionReasonId) {
      formData.set("exemption_reason_id", entity.exemptionReasonId);
    }

    return this.callApi(api => api.all("call_center").all("bulk_exemption_cancellations").postData(formData), {
      ownApiFactory: () => this.apiFactory({ customVersion: "2" }),
      useGenericErrorMessageHandler: false,
    });
  }

  bulkTerminatePreview(entity: BulkExemption) {
    const formData = new FormData();
    formData.set("official_record_id", entity.officialRecordId);
    if (entity.excelFile) {
      formData.set("file", entity.excelFile);
    }
    if (entity.exemptionReasonId) {
      formData.set("exemption_reason_id", entity.exemptionReasonId);
    }

    return this.callApi(
      api =>
        api
          .all("call_center")
          .all("bulk_exemption_cancellation_preview")
          .postData(formData)
          .then(response => response.json()),
      {
        ownApiFactory: () => this.apiFactory({ customVersion: "2" }),
        useGenericErrorMessageHandler: false,
      }
    );
  }
}
